import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChartBar } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";

const money = (v, cur) => `$${Number(v || 0).toFixed(0)}${cur && cur !== "CAD" ? ` ${cur}` : ""}`;

/** v414.0 — store momentum: last 8 weeks of visits / orders / revenue. */
export const StoreMomentumCard = ({ asParams = {} }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/my-store/analytics", { params: asParams })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) return null;
  const maxV = Math.max(1, ...data.weeks.map((w) => w.visits));
  const maxO = Math.max(1, ...data.weeks.map((w) => w.orders));

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="my-store-momentum-card">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-zinc-900 flex items-center gap-1.5">
          <ChartBar size={16} weight="bold" className="text-[#8a6a1c]" />
          {t("momentum.title")}
        </p>
        <p className="text-xs text-zinc-500">
          {t("momentum.totals", { visits: data.totals.visits, orders: data.totals.orders })}
          {" · "}{money(data.totals.revenue, data.currency)}
        </p>
      </div>
      <div className="mt-3 space-y-1.5">
        {data.weeks.map((w) => (
          <div key={w.week_start} className="flex items-center gap-2" data-testid={`momentum-week-${w.week_start}`}>
            <span className="w-12 shrink-0 text-[10px] font-semibold text-zinc-500">{w.label}</span>
            <div className="flex-1 space-y-0.5">
              <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
                <div className="h-full rounded-full bg-[#c9a24a]" style={{ width: `${(w.visits / maxV) * 100}%` }} />
              </div>
              <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
                <div className="h-full rounded-full bg-emerald-500" style={{ width: `${(w.orders / maxO) * 100}%` }} />
              </div>
            </div>
            <span className="w-24 shrink-0 text-right text-[10px] tabular-nums text-zinc-600">
              <b className="text-[#8a6a1c]">{w.visits}</b> {t("momentum.v")} · <b className="text-emerald-700">{w.orders}</b> {t("momentum.o")}
            </span>
            <span className="w-14 shrink-0 text-right text-[10px] tabular-nums font-semibold text-zinc-700">
              {money(w.revenue, "")}
            </span>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-zinc-400 mt-2">
        <span className="inline-block w-2 h-2 rounded-full bg-[#c9a24a] mr-1" />{t("momentum.visits")}
        <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 ml-3 mr-1" />{t("momentum.orders")}
        {" — "}{t("momentum.note")}
      </p>
    </div>
  );
};
